import { useCallback, useEffect, useMemo } from 'react';

import { TableCell, TableRow } from '@mui/material';
import { type Column, flexRender } from '@tanstack/react-table';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import {
  type DragObjectItemInterface,
  DropTypeEnum,
  type RowConfigInterface,
  type RowData,
} from 'components/tables/dndStandardTable/types';
import { useCombinedRefs } from 'hooks/useCombinedRefs';

import { responsiveCellStyles, responsiveRowStyles, useStyles } from './styles';

interface MUIReactDragDropRowProps<RowData> {
  columns: Column<any, unknown>[];
  row: any;
  index: number;
  customClassName?: string;
  isCustomPreview: boolean;
  onCellClick?: (event?: React.MouseEvent<HTMLDivElement, MouseEvent>, rowItem?: RowData, cellIIndex?: number) => void;
  onRowClick?: (event?: React.MouseEvent<HTMLDivElement, MouseEvent>, rowItem?: RowData, rowIndex?: number) => void;
  onRowMouseOver?: (event?: React.MouseEvent<HTMLDivElement, MouseEvent>, rowItem?: RowData, rowIndex?: number) => void;
  onRowMouseOut?: (event?: React.MouseEvent<HTMLDivElement, MouseEvent>, rowItem?: RowData, rowIndex?: number) => void;
  rowConfig?: RowConfigInterface;
  style?: any;
  onContextMenu?: (event?: React.MouseEvent<any>, rowItem?: RowData, cellIIndex?: number) => void;
  onDragStart?(rowItem: RowData): void;
  clickable?: boolean;
  hover?: boolean;
  selected?: boolean;
  resizingEnabled?: boolean;
  onDrop?: (
    dragObject: DragObjectItemInterface<any>,
    droppedItemId: number,
    dropType: DropTypeEnum,
    dropRowId?: string,
  ) => void;
  onDragEnd?(): void;
}
const DragAndDropRow = <T extends RowData>({
  columns,
  row,
  index,
  onDrop,
  rowConfig,
  style,
  customClassName,
  isCustomPreview,
  onCellClick,
  onContextMenu,
  onDragStart,
  onDragEnd,
  onRowMouseOver,
  onRowMouseOut,
  onRowClick,
  clickable = false,
  hover = false,
  selected = false,
  resizingEnabled = false,
}: MUIReactDragDropRowProps<T>) => {
  const { classes, cx } = useStyles();
  const isDraggableRowFromConfig = rowConfig?.draggableRow && rowConfig.draggableRow(row, index);
  const isDroppableRowFromConfig = rowConfig?.droppableRow && rowConfig.droppableRow(row, index);
  const isActive = typeof rowConfig?.isActive === 'function' ? rowConfig?.isActive(row) : false;
  const customDragHandleProvided = !!columns?.find((column) => column.id === 'drag');

  const [{ isDragging }, dragRow, preview] = useDrag({
    type: 'draggableRow',
    item: { item: row, startIndex: index, type: 'draggableRow' } as DragObjectItemInterface<T>,
    canDrag: isDraggableRowFromConfig,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, dropRow] = useDrop({
    accept: 'draggableRow',
    drop: (dragItem) => {
      return onDrop?.(dragItem as DragObjectItemInterface<T>, row.index || index, DropTypeEnum.REORDER_ROWS);
    },
  });

  const combinedRefs = useCombinedRefs(dragRow, dropRow);

  const ref: any = useMemo(() => {
    if (!customDragHandleProvided) {
      if (isDraggableRowFromConfig && isDroppableRowFromConfig) return combinedRefs;
      if (isDraggableRowFromConfig) return dragRow;
      if (isDroppableRowFromConfig) return dropRow;
      return null;
    }
    if (isDroppableRowFromConfig) return dropRow;
    return undefined;
  }, [customDragHandleProvided, isDraggableRowFromConfig, isDroppableRowFromConfig, combinedRefs, dragRow, dropRow]);

  const handleRowClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent> | undefined) => {
      onRowClick?.(event, row, index);
    },
    [onRowClick, row, index],
  );

  const handleOnMouseOver = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent> | undefined) => {
      onRowMouseOver?.(event, row, index);
    },
    [onRowMouseOver, row, index],
  );

  const handleOnMouseOut = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent> | undefined) => {
      onRowMouseOut?.(event, row, index);
    },
    [onRowMouseOut, row, index],
  );

  const handleOnDragStart = useCallback(() => onDragStart?.(row), [onDragStart, row]);

  useEffect(() => {
    isCustomPreview && preview(getEmptyImage(), { captureDraggingState: true });
  }, [isCustomPreview, preview]);

  return (
    <TableRow
      key={row.id}
      ref={ref}
      className={cx(
        responsiveRowStyles(),
        customClassName,
        rowConfig?.className && rowConfig.className,
        clickable && classes.clickable,
        selected && classes.selected,
        isActive && classes.activeRow,
      )}
      hover={hover}
      style={{ ...style, opacity: isDragging ? 0 : 1 }}
      onClick={handleRowClick}
      onDragEnd={onDragEnd}
      onDragStart={handleOnDragStart}
      onMouseLeave={handleOnMouseOut}
      onMouseOver={handleOnMouseOver}
    >
      {row.getVisibleCells().map((cell) => {
        const isDragHandle = cell.column?.id === 'drag';

        return (
          <TableCell
            key={cell.id}
            ref={isDragHandle ? dragRow : undefined}
            className={cx(...(cell.column.columnDef.meta?.cellClasses ?? ''), responsiveCellStyles())}
            style={{ width: resizingEnabled && cell.column.getSize() }}
            onClick={onCellClick}
            onContextMenu={onContextMenu}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

export default DragAndDropRow;
